import * as React from 'react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { Box, Button, CircularProgress, Fade, Typography } from "@mui/material";
import './stripe.css';
import { auth, db, functions } from "../../config/firebase";
import { StripeProductTypes } from "../../utils/enums/StripeProductTypes";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

// Initialize Stripe with public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Firebase callable function
const createCheckoutSession = httpsCallable(functions, 'create_checkout_session');

/**
 * Embedded Stripe checkout form component
 * Handles payment processing for both paid and free users
 */
export const StripeEmbeddedForm = ({
  nftUid,
  publicAddress,
  allChecksPassed,
  setStatus,
  onNextStep,
  productType = StripeProductTypes.NFT,
  licenseUid,
  licenseRequestUid
}) => {
  // Component state
  const [isLoading, setIsLoading] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutInitialized, setCheckoutInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Memoize checkout parameters to avoid recreating on each render
  const checkoutParams = useMemo(() => ({
    nftUid,
    userUid: auth.currentUser?.uid,
    userEmail: auth.currentUser?.email,
    publicAddress,
    productType,
    licenseUid,
    licenseRequestUid
  }), [nftUid, publicAddress, productType, licenseUid, licenseRequestUid]);

  // Check if user is a free user
  const checkUserStatus = useCallback(async () => {
    if (!auth.currentUser) return;

    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const userIsFree = userData.FreeUser === true;
        setIsFreeUser(userIsFree);
        
        // Removed auto-processing for free users
        // Now free users must explicitly click the Continue button
      }
    } catch (error) {
      console.error("Error checking user status:", error);
      setError("Failed to verify user status. Please try again.");
    }
  }, [allChecksPassed, nftUid, publicAddress]);

  // Process free user transaction
  const processFreeUser = useCallback(() => {
    console.log("Processing free user transaction");
    setStatus('success');

    if (onNextStep) {
      onNextStep({
        nftUid,
        publicAddress,
        productType,
        freeUserPayment: true
      });
    }
  }, [nftUid, publicAddress, productType, onNextStep, setStatus]);

  // Initialize checkout session with Stripe
  const initializeCheckout = useCallback(async () => {
    setIsLoading(true);
    setCheckoutInitialized(true);
    setError(null);

    try {
      const result = await createCheckoutSession(checkoutParams);
      
      const data = result.data;
      // Handle paid user case 
      console.log("Result:", result);
      console.log("Client secret received:", data.clientSecret);
      
      // Only set client secret for paid users
      if (!isFreeUser) {
        setClientSecret(data.clientSecret);
      }
      
      // For free users, we just stop the loading state but don't auto-process
      // They will need to click the Continue button explicitly
      
    } catch (error) {
      console.error("Error initializing checkout:", error);
      setError("Failed to initialize checkout. Please try again.");
      setStatus('stripe-error');
      // Keep checkoutInitialized true to prevent infinite loop
    } finally {
      setIsLoading(false);
    }
  }, [checkoutParams, isFreeUser, setStatus]);

  // Check user status on component mount
  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  // Initialize checkout when conditions are met
  useEffect(() => {
    const shouldInitializeCheckout = 
      publicAddress && 
      nftUid && 
      allChecksPassed && 
      !clientSecret && 
      !isLoading && 
      !checkoutInitialized && 
      !error; // Add error check to prevent retries
      
    if (shouldInitializeCheckout) {
      initializeCheckout();
    }
  }, [publicAddress, nftUid, allChecksPassed, clientSecret, isLoading, checkoutInitialized, initializeCheckout, error]);

  // Clean up checkout when component unmounts
  useEffect(() => {
    return () => {
      setClientSecret(null);
      setCheckoutInitialized(false);
    };
  }, []);

  // Don't render anything if checks haven't passed
  if (!allChecksPassed) return null;

  return (
    <Fade in={publicAddress && nftUid && allChecksPassed}>
      <Box sx={{ width: '100%', display: (publicAddress && nftUid && allChecksPassed) ? 'block' : 'none' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ border: '1px solid #f44336', p: 3, borderRadius: 2, my: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error
            </Typography>
            <Typography variant="body2" paragraph>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setError(null);
                setCheckoutInitialized(false);
                initializeCheckout();
              }}
            >
              Try Again
            </Button>
          </Box>
        ) : isFreeUser ? (
          <Box sx={{ border: '1px dashed #ccc', p: 3, borderRadius: 2, my: 2, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Free User: No Payment Required
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              As a free user, no payment is required for this transaction.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={processFreeUser}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>
          </Box>
        ) : clientSecret ? (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={initializeCheckout}
              disabled={isLoading || checkoutInitialized}
            >
              Proceed to Checkout
            </Button>
          </Box>
        )}
      </Box>
    </Fade>
  );
};